import cn from "classnames";
import { FC, PropsWithChildren, ReactElement, ReactNode, useEffect, useRef, useState } from "react";
import { usePopper } from "react-popper";
import { createPortal } from 'react-dom';
import { useMedia } from "react-use";

type HoverTooltipProps = {
    hoverable: ReactNode | ReactElement | string;
    className?: string;
    placement?: Placement;
    offset?: [number, number];
    show?: boolean;
    delayMs?: number;
    popperButtonRef?: React.MutableRefObject<null>;
};

export declare type Placement = "top" | "bottom" | "left" | "right" |
    "top-start" | "top-end" | "bottom-start" | "bottom-end" | "right-start" | "right-end" | "left-start" | "left-end" |
    "auto" | "auto-start" | "auto-end";

export const HoverTooltip: FC<PropsWithChildren<HoverTooltipProps>> = ({ hoverable, children, className, show = false, popperButtonRef: popperButtonRefIn, placement = "top", offset = [0, 10], delayMs }) => {
    const popperButtonRef = useRef(null);
    const popperDivRef = useRef(null);
    const showTimeout = useRef(null as null | NodeJS.Timeout);
    const hideTimeout = useRef(null as null | NodeJS.Timeout);
    // A side placement cannot fit on a phone: the tooltip is wider than the room on either side of
    // its trigger, and Popper's flip falls back to the requested side when no side fits, which put
    // the tooltip 350px past the viewport. Below sm it always opens under the trigger, where
    // preventOverflow keeps it inside the screen.
    const isNarrow = useMedia("(max-width: 639px)");
    const popper = usePopper(popperButtonRefIn?.current ?? popperButtonRef.current, popperDivRef.current, {
        placement: isNarrow ? "bottom" : placement,
        modifiers: [
            {
                name: 'offset',
                options: {
                    offset: offset,
                }
            },
            {
                name: 'preventOverflow',
                options: { padding: 8 },
            },
            {
                name: 'flip',
                options: { fallbackPlacements: isNarrow ? ["top"] : undefined },
            },
        ]
    });
    const [showTooltip, setShowTooltip] = useState(false);
    const visible = showTooltip || show;

    // Hidden means display:none, not visibility:hidden. An invisible tooltip is still laid out, and
    // one placed to the right of a trigger near a phone's edge sat 350px past the viewport, making
    // the document wider than the screen on every page that had one. Popper cannot measure a
    // display:none element, so its position is recomputed the moment it is shown.
    useEffect(() => {
        if (visible) popper.update?.();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible]);

    // Hide is deferred so the pointer can travel across the gap between the trigger and the tooltip
    // (and onto the tooltip itself) without it vanishing — otherwise any link inside is unclickable.
    const HIDE_DELAY_MS = 200;
    const cancelHide = () => {
        if (hideTimeout.current) {
            clearTimeout(hideTimeout.current);
            hideTimeout.current = null;
        }
    }
    const scheduleHide = () => {
        cancelHide();
        hideTimeout.current = setTimeout(() => setShowTooltip(false), HIDE_DELAY_MS);
    }

    const handleMouseOver = () => {
        cancelHide();
        if (delayMs) {
            showTimeout.current = setTimeout(() => setShowTooltip(true), delayMs);
        } else {
            setShowTooltip(true);
        }
    }
    const handleMouseOut = () => {
        if (showTimeout.current) {
            clearTimeout(showTimeout.current);
            showTimeout.current = null;
        }
        scheduleHide();
    }

    // Clear any pending show/hide timers on unmount so a deferred setShowTooltip can't fire on an
    // unmounted component (the hide timer in particular outlives a fast mouse-out → unmount).
    useEffect(() => () => {
        if (showTimeout.current) clearTimeout(showTimeout.current);
        if (hideTimeout.current) clearTimeout(hideTimeout.current);
    }, []);

    return (
        <>
            {(!popperButtonRefIn && children) ?
                <span ref={popperButtonRef} onMouseOver={handleMouseOver} onMouseOut={handleMouseOut}>
                    {hoverable}
                </span>
                :
                hoverable
            }

            {createPortal(<div
                ref={popperDivRef}
                onMouseOver={cancelHide}
                onMouseOut={scheduleHide}
                style={{ ...popper.styles.popper, zIndex: 999, margin: 0 }}
                className={cn("tooltip", (className ?? "block border border-separators-page shadow bg-dialog-background max-w-[min(24rem,calc(100vw-1rem))] p-3"), (visible ? "" : "hidden"))}
                {...popper.attributes.popper}
            >
                {children}
            </div>,
                document.body // This ensures it's outside any stacking contexts
            )}

        </>
    );
}
